/**
 * Avery — chat drawer mounted globally on the operator dashboard.
 *
 * Behavior:
 *   - Closed: renders NOTHING. There is no floating launcher; the omni
 *     "Ask Avery" bar (and Cmd+K) is the only way in. The component stays
 *     mounted so the notification poll and the ASK_EVENT listener keep running.
 *   - Open: centred chat window (up to 880x820, a 12px gutter on phones)
 *   - Conversation persists in localStorage (`avery_widget_v1`) so it survives
 *     reloads and screen navigation
 *   - First-open shows starter suggestions; subsequent opens restore history
 *   - Same /api/avery/chat backend → same tool-use loop + proposal cards
 *
 * Opened from anywhere via askAvery() — the omni bar, Cmd+K, the walkthrough,
 * and the /ask page all use that one escape hatch, so operators can ask Avery
 * questions without losing their place.
 */
import { useEffect, useRef, useState, useCallback, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { useBusiestZoneName } from '../hooks/useBusiestZone'
import { isStaffOnlyScreen, PARKIFY_ADMIN_PATH } from '../auth/screenAccess'
import { filterKeyForPath, setScreenFilter, FILTER_LABELS } from '../lib/screenFilter'
import { looksLikeQuestion } from '../lib/searchIntent'
import {
  STORAGE_KEY, SEEN_NOTIF_KEY, ACTIONED_KEY, averyKey,
  VIEW_AS_KEY, POLL_INTERVAL_MS, ASK_EVENT,
  SUGGESTIONS, ASK_SUGGESTIONS, ASK_FADE_MS, ASK_HOLD_MS,
  LAUNCHPAD_CHIPS, LAUNCHPAD_MORE, chipText,
  type Proposal, type ToolCall, type Notification, type Message, type ProposalStatus, type ClientOption,
  type Attachment, type StagedAttachment, MAX_ATTACHMENTS, ACCEPTED_IMAGE_TYPES,
} from './avery/types'
import {
  paneBackdrop, panelFrame, panelFrameInline, panelInner, panelHeaderInline, panelHeaderModal,
  wordmarkInline, onlineDotInline, iconBtnInline,
  panelBody, panelBodyInline,
  viewAsBar, viewAsLabel, viewAsSelect, viewAsClear,
  composer, composerInlineWrap, composerInput, sendBtn, suggestionBtn, typingDot,
  attachBtn, attachTray, attachChip, attachThumb, attachChipName, attachChipStatus,
  attachChipRemove, attachError,
  launchpadWrap, launchpadGreeting, launchpadGreetingMark, launchpadGreetingText,
  composerLaunchpadWrap, composerLaunchpadInput, composerLaunchpadFoot,
  launchpadOrgPill, launchpadSendBtn, launchpadChipRow, launchpadChip,
  launchpadMoreBtn, launchpadMoreItem, launchpadSectionLabel,
} from './avery/styles'
import {
  rid,
  loadFromStorage, loadSeenNotifications, saveSeenNotifications,
  optimSnoozeActive, setOptimSnooze, askPrefersReducedMotion,
} from './avery/helpers'
import { MessageBubble } from './avery/MessageBubble'
import { runProposal } from './avery/executeProposal'
import { fmtMoney } from '../utils/money'
import { callCs } from '../config/csBase'
import type {
  DashboardSearchPermit as SearchPermit,
  DashboardSearchCurrentResponse as NativeResults,
  DashboardSearchResponse as NativeSearchResponse,
  DashboardSearchSession as SearchSession,
  DashboardSearchTenant as SearchTenant,
  DashboardSearchZone as SearchZone,
} from '@parkify/api-types'
import { callApi } from '../config/apiBase'

// CS (customer-service) API base — same env var used by Customers.tsx

/** Open Avery from anywhere and, if `text` is given, send it as the first message. */
export function askAvery(text?: string) {
  window.dispatchEvent(new CustomEvent(ASK_EVENT, { detail: { text } }))
}

/**
 * A question typed in the bar and handed to the full-page `/ask` surface. It
 * cannot ride ASK_EVENT: the page is not mounted yet when the bar navigates.
 * Whichever chat mounts first takes it, exactly once (the take is synchronous,
 * so a second instance finds it gone), the same hand-off screenFilter.ts uses
 * for list filters. On /ask today that is the DRAWER: AskAveryPage renders the
 * compact <Avery /> whose composer is a button that opens the drawer, not an
 * inline chat. #2949 consumed it only in an inline chat, so prod never sent it.
 */
let pendingAsk: string | null = null
export function queueAsk(text: string) { pendingAsk = text }

/**
 * `inline` renders the SAME chat as the full-page `/avery` surface instead of a
 * floating drawer: in-flow in <main>, always open, no launcher / backdrop /
 * close button (there is nothing behind it to reveal). Everything below the
 * frame — message history, streaming, tool calls, proposal cards and their
 * money-safety paths, the staff "Viewing as" strip, the composer — is shared
 * verbatim. Deliberately a variant rather than an extracted `<AveryChat>`: the
 * chat body owns refund/config proposal execution, and re-plumbing that to gain
 * a second call site would put a money surface back in review for a layout
 * change. See App.tsx for who passes it.
 */
export default function AveryWidget({ inline = false }: { inline?: boolean }) {
  const { authHeaders, user, hasPerm } = useAuth()
  // Which console screen this chat is happening on — sent on every turn so the
  // Worker can gate the client-onboarding tools to the Parkify Admin screen
  // specifically (ADMIN_SCREEN_ONLY_TOOLS in avery/tools.ts). Read fresh via
  // useLocation() rather than cached, so navigating to/from Parkify Admin
  // mid-conversation immediately shows/hides those tools on the next turn.
  const { pathname } = useLocation()
  const [openState, setOpen] = useState(false)
  // Inline IS open — the page can't render a closed chat.
  const open = inline || openState
  const [messages, setMessages] = useState<Message[]>(() => loadFromStorage())
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  // ── Screenshot attachments ───────────────────────────────────────────────
  // Staged attachments for the message being composed. Each is uploaded to R2
  // the moment it's picked/pasted (so the send is instant and the bytes are
  // already server-side), and `previewUrl` is a local object URL used only for
  // the thumbnail — there is deliberately no route that serves an upload back,
  // so the preview lives and dies with this tab.
  const [pending, setPending] = useState<StagedAttachment[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // Focused after a chip fills the composer, so the operator lands in the text
  // ready to edit it — a filled box they then have to click into defeats the
  // point of filling it.
  const composerInputRef = useRef<HTMLInputElement | null>(null)
  const [moreOpen, setMoreOpen] = useState(false)
  // ── Staff "Viewing as" client scope ──────────────────────────────────────
  // Parkify staff (clientId === null) can scope the proactive recommendations
  // AND the chat turn itself (body.clientId → the Worker's scopeForCaller)
  // to a single client. The list comes from GET /api/clients (gated on
  // view.parkify_admin, same as ClientAdmin). Tenant operators never see the
  // picker and the Worker ignores any override for them, so the effective
  // scope below force-nulls for non-staff even if a stale value lingers.
  const isStaff = (user?.clientId ?? null) === null
  const canPickClient = isStaff && hasPerm('view.parkify_admin')
  // Screenshot attachment is internal-only: Parkify staff with the owner/admin
  // permission, the same pair the Worker's uploadScreenshot gate enforces. This
  // flag only hides UI — the server is authoritative and re-checks every call.
  const canAttach = isStaff && hasPerm('view.parkify_admin')
  const [clients, setClients] = useState<ClientOption[]>([])
  const [viewAsClientId, setViewAsClientId] = useState<string | null>(() => {
    try { return localStorage.getItem(VIEW_AS_KEY) || null } catch { return null }
  })
  const scopedClientId = isStaff ? viewAsClientId : null
  // Notification IDs the operator has actioned (clicked CTA) or explicitly
  // dismissed. Persisted across reloads so the badge doesn't keep nagging.
  const [actionedIds, setActionedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(averyKey(ACTIONED_KEY))
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set()
    } catch { return new Set() }
  })
  /**
   * Has the operator actually engaged, as opposed to Avery having spoken first?
   *
   * NOT `messages.length === 0`. Proactive notifications are appended to
   * `messages` on mount and every poll, so on any account with notifications
   * the transcript is never empty — which is why the old empty-state greeting
   * and its suggestion chips almost never rendered. A notification is Avery
   * talking to you unprompted; it is not a conversation, and it must not
   * suppress the surface built to start one.
   */
  const hasConversation = messages.some(m => !m.notification)
  /** The full-page surface, before anyone has typed into it. */
  const launchpad = inline && !hasConversation
  /** Notifications, for the launchpad's "Avery noticed" section. */
  const noticed = messages.filter(m => m.notification).sort((a, b) =>
    (Date.parse(b.notification!.ts) || 0) - (Date.parse(a.notification!.ts) || 0))
  const [showAllNoticed, setShowAllNoticed] = useState(false)
  const visibleNoticed = showAllNoticed ? noticed : noticed.slice(0, 3)
  /** Org name for the composer pill — the demo's cue that Avery is scoped to
   *  a tenant. Staff without a client see nothing rather than a blank chip. */
  const orgLabel = user?.client?.name ?? null
  /** Chips the operator could actually act on. Same gate the screens use. */
  const visibleChips = LAUNCHPAD_CHIPS.filter(c => hasPerm(c.perm))
  const visibleMore = LAUNCHPAD_MORE.filter(m => hasPerm(m.perm))

  // Name the operator's busiest lot in the chips that have a zone subject, so
  // they read like an ask worth making rather than a template to fill in.
  //
  // Fetched ONLY on the launchpad (the drawer has no chips) and ONLY for a role
  // holding view.analytics — "which of your zones earns most" is a revenue
  // ranking, so it rides the permission TOOL_PERMISSION gives the tool that
  // answers it (analyze_zone_revenue). Everyone else keeps the generic wording,
  // which is also what a tenant with no session history sees. Never blocks the
  // paint: the chips render generic and upgrade in place if a name lands.
  const busiestZone = useBusiestZoneName(launchpad && hasPerm('view.analytics'))
  const fillComposer = (text: string) => {
    setInput(text)
    composerInputRef.current?.focus()
  }
  const timeGreeting = (() => {
    const h = new Date().getHours()
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
  })()

  const persistActioned = (next: Set<string>) => {
    setActionedIds(next)
    try { localStorage.setItem(averyKey(ACTIONED_KEY), JSON.stringify([...next])) } catch { /* ignore */ }
  }
  const scrollRef = useRef<HTMLDivElement>(null)

  // Persist messages
  useEffect(() => {
    try {
      localStorage.setItem(averyKey(STORAGE_KEY), JSON.stringify(messages.slice(-50)))
    } catch { /* localStorage may be full */ }
  }, [messages])

  // Auto-scroll on new content — chasing the newest turn is right in a
  // transcript and wrong on the launchpad, where the newest content is a
  // notification card BELOW the greeting and composer. Scrolling to it there
  // pushes the two things the state exists to show off the top of the screen,
  // so the operator lands mid-page on cards with no visible way to type.
  useEffect(() => {
    if (launchpad) return
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, busy, open, launchpad])

  // Staff client list for the "Viewing as" picker. Fetched once; the picker
  // hides itself if the call fails (non-staff or missing view.parkify_admin).
  useEffect(() => {
    if (!canPickClient) return
    let cancelled = false
    void (async () => {
      try {
        const res = await callApi(`/api/clients`, { headers: authHeaders() as Record<string, string> })
        if (!res.ok || cancelled) return
        const list = await res.json() as ClientOption[]
        if (!cancelled) setClients(Array.isArray(list) ? list : [])
      } catch { /* leave picker hidden */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickClient])

  // Proactive notifications — poll on mount, on focus, every 5 min. Re-keyed on
  // scopedClientId so a staff "Viewing as" change tears down the old interval
  // and immediately re-polls for the newly-selected scope.
  useEffect(() => {
    let stopped = false
    const tick = async () => {
      if (stopped) return
      try {
        // Staff-only scope override. Tenant operators always send no param
        // (scopedClientId is force-nulled for them); the Worker locks them to
        // their own client anyway.
        const qs = scopedClientId ? `?clientId=${encodeURIComponent(scopedClientId)}` : ''
        const res = await callApi(`/api/avery/notifications${qs}`, {
          headers: authHeaders() as Record<string, string>,
        })
        // Re-check after each await: if the scope changed (or the widget
        // unmounted) while this request was in flight, this effect was torn
        // down. Bailing here stops a stale-scope response from re-appending
        // the cards onPickClient just stripped (and re-seeding the seen-set).
        if (stopped) return
        if (!res.ok) return
        const data = await res.json() as { notifications: Notification[] }
        if (stopped) return
        const seen = loadSeenNotifications()
        const snoozed = optimSnoozeActive()
        const fresh = data.notifications.filter(n => {
          if (seen.has(n.id)) return false
          // Suppress optimization (high-severity) cards while snooze is active
          if (snoozed && n.severity === 'high') return false
          return true
        })
        if (fresh.length === 0) return
        // Append fresh notifications as proactive Avery messages
        setMessages(prev => [
          ...prev,
          ...fresh.map(n => ({
            id: `notif_${n.id}`,
            role: 'assistant' as const,
            content: `**${n.headline}**\n${n.body}`,
            ts: n.ts,
            notification: n,
          })),
        ])
        // Persist seen IDs
        const next = new Set(seen)
        fresh.forEach(n => next.add(n.id))
        saveSeenNotifications(next)
        // Never auto-open the chat drawer. Fresh findings land in the
        // transcript and wait there — with the floating launcher gone there is
        // no passive out-of-drawer signal at all, which is the trade the
        // removal accepted. Operators enter Avery on their own terms via the
        // omni "Ask Avery" bar (ASK_EVENT) or Cmd+K, so landing on the console
        // after sign-in never pops a panel over their work.
      } catch { /* network blip; try again next tick */ }
    }
    void tick()
    const intervalId = setInterval(() => void tick(), POLL_INTERVAL_MS)
    const onFocus = () => void tick()
    window.addEventListener('focus', onFocus)
    return () => {
      stopped = true
      clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedClientId])

  // Esc to close
  useEffect(() => {
    // Inline has nothing to escape to — Esc would blank the whole screen.
    if (!open || inline) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, inline])

  // Upload one picked/pasted image and stage it for the next send. Each file
  // gets an optimistic chip immediately (uploading:true) so a slow 8 MB PNG
  // shows progress rather than nothing; a failure replaces the chip with the
  // server's own message, which is already operator-readable ("Image is too
  // large (max 8 MB)", "That file is not a PNG…").
  const attachFiles = useCallback(async (files: File[]) => {
    const room = MAX_ATTACHMENTS - pending.length
    if (room <= 0) {
      setUploadError(`You can attach up to ${MAX_ATTACHMENTS} screenshots per message.`)
      return
    }
    if (files.length > room) setUploadError(`Only the first ${room} image(s) were attached — the limit is ${MAX_ATTACHMENTS} per message.`)
    else setUploadError(null)

    for (const file of files.slice(0, room)) {
      const localId = rid()
      const previewUrl = URL.createObjectURL(file)
      setPending(p => [...p, { localId, name: file.name || 'screenshot', previewUrl, uploading: true }])
      try {
        const res = await callApi(`/api/avery/upload`, {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'application/octet-stream', ...(authHeaders() as Record<string, string>) },
          body: file,
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string }
          throw new Error(body.error || `Upload failed (${res.status})`)
        }
        const data = await res.json() as { key: string; mime: string }
        setPending(p => p.map(a => a.localId === localId ? { ...a, uploading: false, key: data.key, mime: data.mime } : a))
      } catch (e) {
        URL.revokeObjectURL(previewUrl)
        setPending(p => p.filter(a => a.localId !== localId))
        setUploadError(e instanceof Error ? e.message : String(e))
      }
    }
  }, [pending.length, authHeaders])

  // Send is live when there's text OR at least one finished upload, and always
  // dead while an upload is still in flight (see send()).
  const sendDisabled = busy
    || pending.some(a => a.uploading)
    || (!input.trim() && !pending.some(a => a.key))

  const removePending = useCallback((localId: string) => {
    setPending(p => {
      const hit = p.find(a => a.localId === localId)
      if (hit) URL.revokeObjectURL(hit.previewUrl)
      return p.filter(a => a.localId !== localId)
    })
  }, [])

  const send = async (text: string) => {
    // An attachment alone is a legitimate turn ("here, look at this"), so the
    // send gate is text OR a finished upload — but never while one is still
    // in flight, which would drop it from the message silently.
    const ready = pending.filter(a => a.key)
    if (pending.some(a => a.uploading)) return
    if ((!text.trim() && !ready.length) || busy) return
    const images: Attachment[] = ready.map(a => ({ key: a.key!, mime: a.mime!, name: a.name }))
    const next: Message[] = [...messages, {
      id: rid(), role: 'user',
      content: text || '(screenshot attached)',
      ts: new Date().toISOString(),
      ...(images.length ? { images } : {}),
    }]
    pending.forEach(a => URL.revokeObjectURL(a.previewUrl))
    setPending([])
    setUploadError(null)
    setMessages(next)
    setInput('')
    setBusy(true)
    try {
      const res = await callApi(`/api/avery/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeaders() as Record<string, string>) },
        // clientId carries the "Viewing as" lens into the turn so Avery's tool
        // reads and proposals stay inside the client shown in the header. The
        // Worker honors it for staff only; tenant callers are pinned regardless.
        body: JSON.stringify({
          messages: next.map(m => ({ role: m.role, content: m.content, ...(m.images?.length ? { images: m.images.map(i => ({ key: i.key, mime: i.mime })) } : {}) })),
          ...(scopedClientId ? { clientId: scopedClientId } : {}),
          ...(pathname === PARKIFY_ADMIN_PATH ? { screen: 'parkify_admin' } : {}),
        }),
      })
      if (!res.ok) {
        const errText = await res.text()
        // The Worker answers `{"error": "<message>"}`; show the message, not the
        // JSON. A busy model (503) is already worded for the operator.
        let message = errText
        try {
          const parsed = JSON.parse(errText) as { error?: unknown }
          if (typeof parsed.error === 'string') message = parsed.error
        } catch { /* not JSON: show it as-is */ }
        const content = res.status === 503 ? message.slice(0, 300) : `_Error: ${message.slice(0, 200)}_`
        setMessages([...next, { id: rid(), role: 'assistant', content, ts: new Date().toISOString() }])
        return
      }
      const data = await res.json() as { reply: string; proposals?: Proposal[]; toolTrace?: ToolCall[] }
      setMessages([...next, {
        id: rid(), role: 'assistant', content: data.reply, ts: new Date().toISOString(),
        proposals: data.proposals, toolTrace: data.toolTrace,
        proposalStatus: {}, proposalError: {},
      }])
      // (chat replies don't bump the badge — the badge tracks outstanding
      // notifications/recommendations only, like an iMessage app icon)
    } catch (e) {
      setMessages([...next, { id: rid(), role: 'assistant', content: `_Error: ${e instanceof Error ? e.message : String(e)}_`, ts: new Date().toISOString() }])
    } finally {
      setBusy(false)
    }
  }

  // Keep a live ref to the latest `send` so the window-event listener below
  // always calls the current closure (fresh messages/busy) without having to
  // re-subscribe on every render.
  const sendRef = useRef(send)
  useEffect(() => { sendRef.current = send })

  // External "Ask Avery" entry point — the Home search bar (and any future
  // surface) dispatches `parkify:avery:ask` to pop the panel open and seed the
  // typed question as the first message.
  useEffect(() => {
    const onAsk = (e: Event) => {
      const text = (e as CustomEvent<{ text?: string }>).detail?.text?.trim()
      setOpen(true)
      if (text) void sendRef.current(text)
    }
    window.addEventListener(ASK_EVENT, onAsk)
    return () => window.removeEventListener(ASK_EVENT, onAsk)
  }, [])

  // Question handed over by the bar's "Ask Avery" row (see queueAsk).
  useEffect(() => {
    if (!pendingAsk) return
    const text = pendingAsk
    pendingAsk = null
    setOpen(true)
    void sendRef.current(text)
  }, [])

  const reset = () => {
    if (!confirm('Clear chat history?')) return
    setMessages([])
    // Only wipe the conversation. SEEN_NOTIF_KEY is server-side dedup state
    // for notification IDs Avery has already surfaced — clearing it would
    // make the next 5-min poll re-add today's stable notifications
    // (permits_expiring_<date>, open_tickets_<date>, …) as if they were
    // fresh, which is the "Avery keeps adding updates after Clear chat"
    // bug. avery_actioned_v1 is kept for the same reason.
    try {
      localStorage.removeItem(averyKey(STORAGE_KEY))
    } catch { /* ignore */ }
  }

  const navigate = (screen: string, notifId?: string) => {
    window.dispatchEvent(new CustomEvent('parkify:navigate', { detail: { screen } }))
    if (notifId) {
      const next = new Set(actionedIds); next.add(notifId); persistActioned(next)
    }
    setOpen(false)
  }

  // Staff picks the client whose recommendations Avery should show. Pass null
  // (or the "All clients" option) to return to the portfolio view.
  //
  // Switching scope must RESET the proactive-notification view, not just retag
  // the poll: notification IDs aren't client-namespaced (cs cards are
  // `<kind>_<zone>_<date>`, KV cards `<kind>_<date>`), so the same id can
  // appear under both the all-clients and a single-client view. Without a
  // reset the seen-set would suppress the newly-scoped cards as "already
  // seen" and the stale all-clients cards would linger in the transcript. So
  // we clear the transcript, clear the seen + actioned sets, then let the poll
  // effect (re-keyed on scopedClientId) re-fetch the selected scope's cards
  // fresh.
  //
  // The human Q&A is cleared too, and that is deliberate. Now that the lens
  // really scopes the turn, keeping it would defeat the point: every turn
  // re-sends the whole transcript, so the previous client's zones, revenue and
  // plates would ride along as model context into a turn labelled with the NEW
  // client. A scope switch is a clean slate or it isn't a scope switch.
  const onPickClient = (id: string | null) => {
    if (id === viewAsClientId) {
      setMessages(prev => prev.filter(m => !m.notification))
    } else {
      setMessages([{
        id: rid(), role: 'assistant', ts: new Date().toISOString(),
        content: '_Scope changed — starting a fresh conversation so nothing carries over from the previously selected client._',
      }])
    }
    try { localStorage.removeItem(averyKey(SEEN_NOTIF_KEY)) } catch { /* ignore */ }
    persistActioned(new Set())
    setViewAsClientId(id)
    try {
      if (id) localStorage.setItem(VIEW_AS_KEY, id)
      else localStorage.removeItem(VIEW_AS_KEY)
    } catch { /* ignore */ }
  }

  // Mark a proactive notification as handled so it stops counting toward the
  // unread badge — fired on navigate, dismiss, or a successful Approve.
  const markActioned = (notifId: string) => {
    const next = new Set(actionedIds); next.add(notifId); persistActioned(next)
  }

  const dismissNotification = (notifId: string) => {
    markActioned(notifId)
    // If this was the last undismissed optimization card, start the 24hr snooze
    // so the operator isn't immediately re-bombarded on the next poll.
    setMessages(prev => {
      const nextActioned = new Set(actionedIds); nextActioned.add(notifId)
      const remainingOptims = prev.filter(
        m => m.notification?.severity === 'high' && !nextActioned.has(m.notification.id)
      )
      if (remainingOptims.length === 0) setOptimSnooze()
      return prev   // no state change — side-effect only
    })
  }

  const setProposalStatus = (msgId: string, idx: number, status: ProposalStatus, errMsg?: string) => {
    setMessages(prev => prev.map(m => {
      if (m.id !== msgId) return m
      return {
        ...m,
        proposalStatus: { ...(m.proposalStatus ?? {}), [idx]: status },
        proposalError: errMsg ? { ...(m.proposalError ?? {}), [idx]: errMsg } : m.proposalError,
      }
    }))
  }

  const executeProposal = async (msgId: string, idx: number, p: Proposal) => {
    setProposalStatus(msgId, idx, 'executing')
    try {
      await runProposal(p, authHeaders, `${msgId}:${idx}`, user)
      setProposalStatus(msgId, idx, 'done')
      return true
    } catch (e) {
      setProposalStatus(msgId, idx, 'error', e instanceof Error ? e.message : String(e))
      return false
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes avery-typing-bounce {
          0%, 80%, 100% { transform: translateY(0);  opacity: 0.5; }
          40%           { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes avery-bubble-in {
          0%   { opacity: 0; transform: translateY(6px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes avery-pane-in {
          0%   { transform: translateY(12px) scale(0.98); opacity: 0; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes avery-backdrop-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
      {/* No collapsed surface. The bottom-right floating launcher (and the
          proactive-update bubble that sat above it) were removed 2026-08-25:
          the omni "Ask Avery" bar — on every screen, plus Cmd+K — is the entry
          point, and a second always-on door over the console content earned
          nothing. This component now renders ONLY the open drawer; it stays
          mounted while closed for the notification poll and the ASK_EVENT
          listener that opens it.

          Consequence, deliberate: proactive notifications no longer have a
          passive signal. They are still appended to the transcript, so the
          operator sees them on their next visit to Avery, but nothing outside
          the drawer counts them. If that signal is wanted back, it belongs on
          the omni bar, not on a new floating element. */}

      {/* Chat window — centred over the console, like a standard chat app.
          A semi-transparent backdrop dims the main content behind it;
          clicking the backdrop closes the window (same as Esc). */}
      {open && (
        <>
          {/* No backdrop inline — there's no main content to dim; the chat IS it. */}
          {!inline && (
            <div
              style={paneBackdrop}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
          )}
          <div style={inline ? { ...panelFrameInline, ...(launchpad ? { height: 'auto', minHeight: 0, background: 'transparent' } : {}) } : panelFrame}>
           <div style={launchpad ? { ...panelInner, height: 'auto', overflow: 'visible' } : panelInner}>
          {/* Header — a plain bordered title bar on both surfaces, so the
              popped-out window and the in-page `/avery` screen read as the same
              chat. The window adds "Open full page" (to `/avery`) and close.
              Suppressed on the launchpad: the greeting below IS the header, and
              "Clear chat" has nothing to clear before the first turn. */}
          {!launchpad && (
          <div style={inline ? panelHeaderInline : panelHeaderModal}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={wordmarkInline}>avery</strong>
              <span style={onlineDotInline} aria-label="online" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {messages.length > 0 && (
                <button onClick={reset} style={iconBtnInline} aria-label="Clear chat" title="Clear chat">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              )}
              {!inline && (
                <button onClick={() => navigate('avery')} style={iconBtnInline} aria-label="Open full page" title="Open full page">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                  </svg>
                </button>
              )}
              {!inline && (
                <button onClick={() => setOpen(false)} style={iconBtnInline} aria-label="Close" title="Close">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
          )}

          {/* Staff "Viewing as" client scope — only Parkify staff with
              view.parkify_admin and a loaded client list. Picking a client
              scopes Avery's proactive recommendations to it; "All clients"
              returns the portfolio view. Hidden entirely for tenant operators. */}
          {canPickClient && clients.length > 0 && (
            <div style={viewAsBar}>
              <span style={viewAsLabel}>Viewing as</span>
              <select
                value={viewAsClientId ?? ''}
                onChange={e => onPickClient(e.target.value || null)}
                style={viewAsSelect}
                aria-label="Scope Avery to a client"
              >
                <option value="">All clients</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.internal ? ' (internal)' : ''}</option>
                ))}
              </select>
              {viewAsClientId && (
                <button
                  onClick={() => onPickClient(null)}
                  style={viewAsClear}
                  aria-label="Clear client filter"
                  title="Clear — view all clients"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div ref={scrollRef} style={launchpad ? { ...panelBodyInline, overflowY: 'visible', padding: 0, flex: 'none', background: 'transparent' } : inline ? panelBodyInline : panelBody}>
            {launchpad ? (
              <div style={launchpadWrap}>
                <div style={launchpadGreeting}>
                  <span style={launchpadGreetingMark} aria-hidden="true">✦</span>
                  <span style={launchpadGreetingText}>
                    {timeGreeting}, {user?.name?.split(' ')[0] ?? 'there'}
                  </span>
                </div>

                {renderComposer()}

                {/* Tapping a chip FILLS the box rather than sending it: the
                    operator sees what a good ask looks like and can edit it,
                    which teaches in a way a button that just fires cannot. */}
                <div style={launchpadChipRow}>
                  {visibleChips.map(c => (
                    <button key={c.label} type="button" onClick={() => fillComposer(chipText(c, busiestZone))} style={launchpadChip}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--parkify-accent)"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {c.icon.split(' M').map((d, i) => <path key={i} d={(i ? 'M' : '') + d} />)}
                      </svg>
                      {c.label}
                    </button>
                  ))}
                  {visibleMore.length > 0 && (
                    <button type="button" onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen} style={launchpadMoreBtn}>
                      {moreOpen ? 'Less' : 'More…'}
                    </button>
                  )}
                </div>

                {moreOpen && visibleMore.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                    {visibleMore.map(m => (
                      <button key={m.text} type="button" onClick={() => { fillComposer(chipText(m, busiestZone)); setMoreOpen(false) }} style={launchpadMoreItem}>
                        “{chipText(m, busiestZone)}”
                      </button>
                    ))}
                  </div>
                )}

                {/* The proactive half of Avery. These are the same notification
                    messages the transcript renders — reusing MessageBubble keeps
                    one card, so severity, CTA gating and one-tap Approve can't
                    drift between the two states. */}
                {noticed.length > 0 && (
                  <>
                    <div style={launchpadSectionLabel}>Avery noticed</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {visibleNoticed.map(m => (
                        <MessageBubble
                          key={m.id}
                          notificationCard
                          message={m}
                          onExecute={(idx, p) => { void executeProposal(m.id, idx, p).then(ok => { if (ok && m.notification) markActioned(m.notification.id) }) }}
                          onDismiss={(idx) => { setProposalStatus(m.id, idx, 'dismissed'); if (m.notification) markActioned(m.notification.id) }}
                          onNavigate={navigate}
                          onAskFollowup={fillComposer}
                          onDismissNotification={dismissNotification}
                          isActioned={!!m.notification && actionedIds.has(m.notification.id)}
                        />
                      ))}
                    </div>
                    {noticed.length > 3 && (
                      <button type="button" onClick={() => setShowAllNoticed(value => !value)}
                        aria-expanded={showAllNoticed}
                        style={{ ...launchpadMoreBtn, alignSelf: 'flex-start', marginTop: 12 }}>
                        {showAllNoticed ? 'Show fewer insights' : `Show all ${noticed.length} insights`}
                      </button>
                    )}
                  </>
                )}
              </div>
            ) : messages.length === 0 ? (
              <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{
                  alignSelf: 'flex-start', maxWidth: '90%',
                  background: 'var(--color-white)', color: 'var(--color-black)',
                  border: '1px solid var(--color-brand-border)',
                  boxShadow: '0 1px 3px color-mix(in srgb, var(--parkify-accent) 8%, transparent)',
                  borderRadius: 14, borderBottomLeftRadius: 4,
                  padding: '11px 15px', fontSize: 15, lineHeight: 1.55,
                }}>
                  Hey {user?.name?.split(' ')[0] ?? 'there'} 👋 — tap a suggestion below or just ask me anything about your dashboard. I can also tee up actions like refunds for you to confirm.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => void send(s)} style={suggestionBtn}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {messages.map(m => (
                  <MessageBubble
                    key={m.id}
                    notificationCard={inline}
                    message={m}
                    onExecute={(idx, p) => { void executeProposal(m.id, idx, p).then(ok => { if (ok && m.notification) markActioned(m.notification.id) }) }}
                    onDismiss={(idx) => { setProposalStatus(m.id, idx, 'dismissed'); if (m.notification) markActioned(m.notification.id) }}
                    onNavigate={navigate}
                    onAskFollowup={(text) => void send(text)}
                    onDismissNotification={dismissNotification}
                    isActioned={!!m.notification && actionedIds.has(m.notification.id)}
                  />
                ))}
                {busy && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{
                      background: 'var(--color-white)',
                      border: '1px solid var(--color-brand-border)',
                      boxShadow: '0 1px 3px color-mix(in srgb, var(--parkify-accent) 8%, transparent)',
                      borderRadius: 14, borderBottomLeftRadius: 4,
                      padding: '11px 15px', display: 'inline-flex', gap: 4,
                    }} aria-label="Avery is typing">
                      <span style={typingDot(0)} />
                      <span style={typingDot(0.15)} />
                      <span style={typingDot(0.3)} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Staged screenshot chips + upload errors. Rendered above the
              composer so a thumbnail never squeezes the text field. */}
          {canAttach && (pending.length > 0 || uploadError) && (
            <div style={attachTray}>
              {pending.map(a => (
                <div key={a.localId} style={attachChip} title={a.name}>
                  <img src={a.previewUrl} alt="" style={attachThumb} />
                  <span style={attachChipName}>{a.name}</span>
                  {a.uploading
                    ? <span style={attachChipStatus}>uploading…</span>
                    : (
                      <button
                        type="button"
                        onClick={() => removePending(a.localId)}
                        aria-label={`Remove ${a.name}`}
                        style={attachChipRemove}
                      >×</button>
                    )}
                </div>
              ))}
              {uploadError && <div style={attachError} role="alert">{uploadError}</div>}
            </div>
          )}

          {/* Composer — docked. In the launchpad state it is rendered inside
              the centred column instead (see renderComposer); never both. */}
          {!launchpad && renderComposer()}
           </div>
          </div>
        </>
      )}
    </>
  )

  /* ONE definition of each control, composed into two layouts, rather than a
     second copy of the form in the launchpad branch. The docked variant is a
     row (attach, pill input, Send); the launchpad variant is a card (borderless
     input above a footer of attach, org pill and a round submit). The parts
     carry the attach/paste path and the disabled rules, and a divergent
     duplicate is how one position quietly stops accepting screenshots. */
  function renderComposer() {
    const hiddenFileInput = canAttach ? (
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        multiple
        hidden
        onChange={e => {
          void attachFiles(Array.from(e.target.files ?? []))
          // Reset so picking the SAME file twice in a row still fires
          // change — otherwise a re-attach after a remove is a no-op.
          e.target.value = ''
        }}
      />
    ) : null

    const attachButton = canAttach ? (
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={busy || pending.length >= MAX_ATTACHMENTS}
        aria-label="Attach a screenshot"
        title="Attach a screenshot (or just paste one)"
        style={{
          ...attachBtn,
          opacity: busy || pending.length >= MAX_ATTACHMENTS ? 0.35 : 1,
          cursor: busy || pending.length >= MAX_ATTACHMENTS ? 'not-allowed' : 'pointer',
        }}
      >📎</button>
    ) : null

    const textInput = (
      <input
        autoFocus
        ref={composerInputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onPaste={canAttach ? e => {
          // Cmd-V straight from a screenshot tool is the primary path —
          // the 📎 button is the fallback, not the other way round.
          const files = Array.from(e.clipboardData?.files ?? []).filter(f => f.type.startsWith('image/'))
          if (!files.length) return
          e.preventDefault()
          void attachFiles(files)
        } : undefined}
        // Full `border`, not a borderColor override: composerInput sets the
        // shorthand, and mixing the two makes React warn and can leave the
        // colour stuck on a rerender.
        onFocus={launchpad ? undefined : e => { e.currentTarget.style.border = '1.5px solid var(--parkify-accent)'; e.currentTarget.style.background = 'var(--color-white)' }}
        onBlur={launchpad ? undefined : e => { e.currentTarget.style.border = '1.5px solid var(--color-brand-border)'; e.currentTarget.style.background = 'var(--color-gray-100)' }}
        placeholder={launchpad ? 'Ask Avery anything, or start a task…' : 'Type a message…'}
        disabled={busy}
        style={launchpad ? composerLaunchpadInput : composerInput}
      />
    )

    if (launchpad) {
      return (
        <form onSubmit={e => { e.preventDefault(); void send(input) }} style={composerLaunchpadWrap}>
          {hiddenFileInput}
          {textInput}
          <div style={composerLaunchpadFoot}>
            {attachButton}
            {orgLabel && <span style={launchpadOrgPill} title={orgLabel}>{orgLabel}</span>}
            <button
              type="submit"
              disabled={sendDisabled}
              aria-label="Send"
              style={{
                ...launchpadSendBtn,
                background: sendDisabled ? 'var(--color-gray-400)' : 'var(--parkify-accent)',
                cursor: sendDisabled ? 'not-allowed' : 'pointer',
              }}
            >↑</button>
          </div>
        </form>
      )
    }

    return (
      <form onSubmit={e => { e.preventDefault(); void send(input) }} style={inline ? composerInlineWrap : composer}>
        {hiddenFileInput}
        {attachButton}
        {textInput}
        <button
          type="submit"
          disabled={sendDisabled}
          aria-label="Send"
          style={{
            ...sendBtn,
            ...(inline ? { background: 'var(--parkify-accent)', boxShadow: 'none' } : {}),
            opacity: sendDisabled ? 0.35 : 1,
            cursor: sendDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Send
        </button>
      </form>
    )
  }
}

// ─── Search result types ──────────────────────────────────────────────────────

interface CsCustomer {
  id: number
  email: string | null
  phone: string | null
  name: string | null
}
interface CsListResp { customers: CsCustomer[] }
// Disputes are Stripe-backed (no server search param), so the bar fetches the
// list once, caches it, and filters client-side. `hay` is the lowercased
// searchable blob (id + reason + metadata, incl. payment_intent metadata where
// plate/email live).
interface SearchDispute { id: string; reason: string; status: string; amountCents: number; hay: string }

// ─── AskAveryBar ─────────────────────────────────────────────────────────────
// Global "Ask Avery" pill bar mounted at the top of every console screen.
// Submitting opens the floating Avery widget seeded with the typed question.

export function AskAveryBar() {
  const { authHeaders, user } = useAuth()
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [idx, setIdx] = useState(0)
  const [phVisible, setPhVisible] = useState(true)

  // ── Search results state (each source independent) ──────────────────────────
  const [nativeResults, setNativeResults] = useState<NativeResults | null>(null)
  const [customers, setCustomers] = useState<CsCustomer[]>([])
  const [disputes, setDisputes] = useState<SearchDispute[]>([])
  const [searching, setSearching] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  // Keyboard navigation: index of the highlighted dropdown row (-1 = none).
  const [activeIndex, setActiveIndex] = useState(-1)

  // Refs for debounce timer and click-outside detection
  const debounceRef = useRef<number | undefined>(undefined)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Carries the clicked result's term across the navigation so the destination
  // bar shows what it's filtered by (instead of going blank). Consumed by the
  // route-change effect on arrival.
  const carryValueRef = useRef<string | null>(null)
  // Fetch-once cache of disputes (Stripe list), filtered client-side per search.
  const disputesCacheRef = useRef<SearchDispute[] | null>(null)

  // When the active screen has a power-bar-driven list filter, typing live-filters
  // that screen; otherwise the bar is purely "ask Avery". filterKey is null on
  // non-filterable screens (Home, Analytics, dashboards, etc.).
  const { pathname, search: locationSearch } = useLocation()
  const filterKey = filterKeyForPath(pathname)

  const reduceMotion = askPrefersReducedMotion()
  const showPlaceholder = value.length === 0 && !focused

  // Leaving a filterable screen (or arriving on one) must not strand a filter or
  // carry stale text across screens: clear the box and release the prior filter.
  const prevFilterKey = useRef<string | null>(filterKey)
  useEffect(() => {
    if (prevFilterKey.current && prevFilterKey.current !== filterKey) {
      setScreenFilter(prevFilterKey.current, '')
    }
    prevFilterKey.current = filterKey
    // If we arrived via a global-search click-through, show the carried term so
    // the destination bar reflects the active filter. A link that lands already
    // filtered (/transactions?q=…, which the screen applies itself) shows its
    // term the same way; otherwise start clean.
    if (carryValueRef.current != null) {
      setValue(carryValueRef.current)
      carryValueRef.current = null
    } else {
      setValue(filterKey ? new URLSearchParams(locationSearch).get('q') ?? '' : '')
    }
    setDropdownOpen(false)
    setNativeResults(null)
    setCustomers([])
    setDisputes([])
    // Read ?q= on arrival only: the screen rewrites it as the operator types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, filterKey])

  const onChange = (next: string) => {
    setValue(next)
    // Intent gate: a lookup term live-filters the current list; a natural-language
    // question does NOT (releasing the filter with '' so the table repopulates
    // instead of scrambling as you type the question).
    if (filterKey) setScreenFilter(filterKey, looksLikeQuestion(next) ? '' : next)
    // Trigger / cancel dropdown search (runs regardless of intent — the dropdown
    // shows results AND an explicit "Ask Avery" row).
    if (next.length < 2) {
      window.clearTimeout(debounceRef.current)
      setDropdownOpen(false)
      setNativeResults(null)
      setCustomers([])
      setDisputes([])
      setSearching(false)
      return
    }
    window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => { void runSearch(next) }, 200)
  }

  // Fan-out: fire both sources in parallel with Promise.allSettled so a slow
  // or failing CS source never blocks the native entities from rendering.
  const runSearch = useCallback(async (q: string) => {
    setSearching(true)
    setDropdownOpen(true)

    // Disputes — independent lazy section. Stripe-backed with no search param, so
    // fetch the list once (cache it), then filter client-side. Fire-and-forget so
    // it fills in when ready and never blocks the fast native/CS results.
    const needle = q.toLowerCase()
    void (async () => {
      // Disputes are staff-only for now, and this section had NO permission gate
      // of its own — it surfaced dispute rows to any role that could open the
      // search bar. Skip the fetch entirely rather than filtering after the fact,
      // so a client-scoped operator's search never touches the endpoint.
      if (isStaffOnlyScreen('disputes', user)) { setDisputes([]); return }
      if (!disputesCacheRef.current) {
        try {
          const r = await callApi(`/api/disputes`, { headers: authHeaders() as Record<string, string> })
          const data = r.ok ? (await r.json() as { data?: Array<Record<string, unknown>> }) : { data: [] }
          disputesCacheRef.current = (data.data ?? []).map(d => {
            const pi = d.payment_intent
            const piMeta = pi && typeof pi === 'object' ? (pi as { metadata?: unknown }).metadata : undefined
            return {
              id: String(d.id ?? ''),
              reason: String(d.reason ?? ''),
              status: String(d.status ?? ''),
              amountCents: Number(d.amount ?? 0),
              hay: JSON.stringify({ id: d.id, reason: d.reason, meta: d.metadata, pi: piMeta }).toLowerCase(),
            }
          })
        } catch { disputesCacheRef.current = [] }
      }
      setDisputes((disputesCacheRef.current ?? []).filter(d => d.hay.includes(needle)).slice(0, 6))
    })()

    // Both fetches start at exactly the same time (not awaited in sequence)
    const nativeProm = callApi(
      `/api/dashboard/search?q=${encodeURIComponent(q)}&limit=6`,
      { headers: authHeaders() as Record<string, string> },
    ).then(r => r.ok ? r.json() as Promise<NativeSearchResponse> : Promise.reject(new Error(`HTTP ${r.status}`)))

    const csProm = callCs(
      `/admin/customers?search=${encodeURIComponent(q)}`,
      { headers: authHeaders() as Record<string, string> },
    ).then(r => r.ok ? r.json() as Promise<CsListResp> : Promise.reject(new Error(`HTTP ${r.status}`)))

    const [nativeSettled, csSettled] = await Promise.allSettled([nativeProm, csProm])

    if (nativeSettled.status === 'fulfilled') {
      // Normalize: tolerate a response missing any category (older API builds,
      // or a partial payload) so the render's `.length`/`.map` never hit undefined.
      const v = nativeSettled.value ?? {}
      setNativeResults({
        sessions: v.sessions ?? [],
        zones: v.zones ?? [],
        permits: v.permits ?? [],
        tenants: v.tenants ?? [],
      })
    } else {
      setNativeResults({ sessions: [], zones: [], permits: [], tenants: [] })
    }
    if (csSettled.status === 'fulfilled') {
      setCustomers((csSettled.value.customers ?? []).slice(0, 6))
    } else {
      setCustomers([])
    }
    setSearching(false)
  // authHeaders is stable (useCallback in AuthContext) — safe to omit from deps.
  // `user` is NOT stable and must stay listed: it's null on first render and
  // changes again on "Viewing as" (#1361). Captured stale, the staff gate above
  // would read null forever and hide disputes from Parkify staff too.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Close dropdown on click-outside
  useEffect(() => {
    if (!dropdownOpen) return
    const onPointerDown = (e: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [dropdownOpen])

  // Close dropdown on Escape (does NOT close the Avery pane — that's separate)
  useEffect(() => {
    if (!dropdownOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDropdownOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dropdownOpen])

  // Click-through helpers: navigate to screen and pre-filter where supported,
  // then close the dropdown and clear the bar.
  const resetDropdown = () => {
    setDropdownOpen(false)
    setNativeResults(null)
    setCustomers([])
    setDisputes([])
  }

  // Navigate to a screen pre-filtered by `term`, and surface `term` in the
  // destination bar. Two paths:
  //  • cross-screen — stash in carryValueRef; the route-change effect sets the
  //    value on arrival (and the Phase-2 pending-filter map drives the filter).
  //  • same-screen — navigate is a no-op, so set value + filter directly.
  // We deliberately do NOT clear the origin filter here: a cross-screen jump's
  // origin is released by the route-change effect, and a same-screen click must
  // keep the filter we just set.
  const goFiltered = (path: string, key: string, term: string) => {
    setScreenFilter(key, term)
    if (path === pathname) {
      setValue(term)
    } else {
      carryValueRef.current = term
      setValue('')
      navigate(path)
    }
    resetDropdown()
  }
  const goSession = (s: SearchSession) => goFiltered('/transactions', 'transactions', s.plate || s.txnId)
  const goZone    = (z: SearchZone)    => goFiltered('/zones', 'zones', z.displayName)
  // A driver hit carries the operator's own words through to the Permits screen
  // (whose filter and Drivers tab both match on driver name), so clicking
  // "Marcus Bell" lands on Marcus Bell's cars rather than on every car the
  // company permit covers. Any other hit filters by label as before.
  const goPermit  = (p: SearchPermit)  => goFiltered('/permits', 'permits', p.match?.driver ?? p.label)
  const goTenant  = (t: SearchTenant)  => goFiltered('/tenants', 'tenants', t.name)
  // Customers is now a filterable screen too — pre-filter it (it debounce-loads
  // the CS API on the carried term).
  const goCustomer = (c: CsCustomer) =>
    goFiltered('/customers', 'customers', c.name || c.email || c.phone || String(c.id))

  // Explicit "Ask Avery" action from the dropdown row: navigate to the
  // full-page Ask Avery surface with the question, and release any in-place
  // filter. The legacy drawer remains available from the contextual shortcut,
  // but this explicit destination must not send operators back to /avery.
  const goAsk = () => {
    if (filterKey) setScreenFilter(filterKey, '')
    // Carry the typed question with it. Navigating alone opened an empty
    // composer, so the operator had to type the question a second time.
    const text = value.trim()
    if (text) queueAsk(text)
    navigate('/ask')
    setValue('')
    resetDropdown()
  }

  // Derived: total hit count across all sources
  const totalHits =
    (nativeResults?.sessions.length ?? 0) +
    (nativeResults?.zones.length ?? 0) +
    (nativeResults?.permits.length ?? 0) +
    (nativeResults?.tenants.length ?? 0) +
    customers.length +
    disputes.length

  // Intent: a question routes to Avery (and we lead the dropdown with the Ask
  // row); a lookup leads with results and offers Ask as a quiet footer.
  const asking = looksLikeQuestion(value)

  const hasSearched = nativeResults !== null  // at least one source resolved

  // ── Keyboard navigation model ──────────────────────────────────────────────
  // Flatten every selectable result row (excludes the special Ask row) in visual
  // order, so arrow keys can walk them and Enter can select the highlighted one.
  type ResultRow = { section: string; key: string; primary: React.ReactNode; secondary?: React.ReactNode; onSelect: () => void }
  const resultRows: ResultRow[] = []
  ;(nativeResults?.sessions ?? []).forEach(s => resultRows.push({
    section: 'Sessions', key: `session:${s.txnId}`,
    primary: (<><code style={{ fontFamily: 'monospace', fontWeight: 700 }}>{s.plate}</code>{s.zoneName ? ` · ${s.zoneName}` : s.zoneId ? ` · ${s.zoneId}` : ''}</>),
    secondary: `${fmtMoney(s.amountCents)} · ${s.txnId}`,
    onSelect: () => goSession(s),
  }))
  ;(nativeResults?.zones ?? []).forEach(z => resultRows.push({
    section: 'Zones', key: `zone:${z.id}`, primary: z.displayName, secondary: z.address, onSelect: () => goZone(z),
  }))
  ;(nativeResults?.permits ?? []).forEach(p => {
    const m = p.match
    // "+2 more" when the needle hit several of this permit's cars (a shared
    // surname, or a description every van on the permit shares).
    const more = (p.matchCount ?? 0) > 1 ? ` +${p.matchCount! - 1} more` : ''
    resultRows.push({
      section: 'Permits', key: `permit:${p.id}`,
      // Driver hit → "Marcus Bell · GA2WHL663", with the permit label demoted to
      // the secondary line. Plate- or description-only hit keeps the label up top
      // (there's no name to lead with) and surfaces the car underneath.
      primary: m?.driver
        ? (<>{m.driver} · <code style={{ fontFamily: 'monospace', fontWeight: 700 }}>{m.plate}</code>{more}</>)
        : p.label,
      secondary: m
        ? [m.description, m.driver ? p.label : `${m.plate}${more}`].filter(Boolean).join(' · ')
        : (p.plates ?? []).join(' · '),
      onSelect: () => goPermit(p),
    })
  })
  ;(nativeResults?.tenants ?? []).forEach(t => resultRows.push({
    section: 'Tenants', key: `tenant:${t.id}`, primary: t.name, secondary: t.contactEmail, onSelect: () => goTenant(t),
  }))
  disputes.forEach(d => resultRows.push({
    section: 'Disputes', key: `dispute:${d.id}`,
    primary: (<><code style={{ fontFamily: 'monospace', fontWeight: 700 }}>{d.id}</code>{d.reason ? ` · ${d.reason.replace(/_/g, ' ')}` : ''}</>),
    secondary: `${fmtMoney(d.amountCents)} · ${d.status.replace(/_/g, ' ')}`,
    onSelect: () => goFiltered('/disputes', 'disputes', d.id),
  }))
  customers.forEach(c => resultRows.push({
    section: 'Customers', key: `customer:${c.id}`,
    primary: c.name ?? c.email ?? c.phone ?? `Customer ${c.id}`,
    secondary: (c.email || c.phone) ? [c.email, c.phone].filter(Boolean).join(' · ') : undefined,
    onSelect: () => goCustomer(c),
  }))

  // The Ask row leads (question intent) or trails as a footer (lookup intent).
  const showFooterAsk = !asking && !searching && value.trim().length >= 2
  const askIndex = asking ? 0 : (showFooterAsk ? resultRows.length : -1)
  const firstResultIndex = asking ? 1 : 0
  const itemCount = resultRows.length + (askIndex >= 0 ? 1 : 0)

  const selectActive = (): boolean => {
    if (activeIndex < 0) return false
    if (activeIndex === askIndex) { goAsk(); return true }
    const r = resultRows[activeIndex - firstResultIndex]
    if (r) { r.onSelect(); return true }
    return false
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!dropdownOpen || itemCount === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, itemCount - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); selectActive() }
  }

  // Stale highlight guard: reset when the result set or query changes.
  useEffect(() => { setActiveIndex(-1) }, [value, nativeResults, customers, disputes, dropdownOpen])

  useEffect(() => {
    // On filterable screens the placeholder is a static "Filter …" hint, so skip
    // the rotation (it would imply you can only ask, not filter).
    if (!showPlaceholder || reduceMotion || filterKey) { setPhVisible(true); return }
    let fadeTimer: number | undefined
    const interval = window.setInterval(() => {
      setPhVisible(false)
      fadeTimer = window.setTimeout(() => {
        setIdx(i => (i + 1) % ASK_SUGGESTIONS.length)
        setPhVisible(true)
      }, ASK_FADE_MS)
    }, ASK_HOLD_MS)
    return () => { window.clearInterval(interval); if (fadeTimer) window.clearTimeout(fadeTimer) }
  }, [showPlaceholder, reduceMotion, filterKey])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    // On a filterable screen (Transactions among them), Enter on a plain
    // lookup — not a question — commits the search rather than defaulting to
    // Ask Avery: the text is already live-filtering (and, on Transactions,
    // already driving a debounced server search) via onChange, so Enter just
    // closes the dropdown and leaves it running. Avery stays one click away
    // via the explicit "Ask Avery" dropdown row (or arrow-key select + Enter).
    if (filterKey && !asking) {
      setDropdownOpen(false)
      return
    }
    setDropdownOpen(false)
    askAvery(value.trim() || undefined)
    // Hand-off to Avery: drop any live filter so the screen's list repopulates.
    if (filterKey) setScreenFilter(filterKey, '')
    setValue('')
  }

  // ── Dropdown section styles (reused per category) ──────────────────────────
  const sectionHeader: React.CSSProperties = {
    padding: '6px 12px 3px',
    fontSize: 10, fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase',
    color: 'var(--parkify-accent)', fontFamily: "'Montserrat', 'Avenir', sans-serif",
  }
  const resultRow: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 1,
    padding: '7px 12px', cursor: 'pointer',
    borderRadius: 'var(--radius-chip)', margin: '0 4px',
    background: 'transparent', border: 'none', textAlign: 'left',
    width: 'calc(100% - 8px)', fontFamily: "'Montserrat', 'Avenir', sans-serif",
  }
  const rowPrimary: React.CSSProperties = {
    fontSize: 13, fontWeight: 600, color: 'var(--color-black)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }
  const rowSecondary: React.CSSProperties = {
    fontSize: 11, color: 'var(--color-gray-500)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }

  // Explicit "Ask Avery: …" row. `primary` = lead the dropdown (question intent);
  // otherwise it's a quiet footer below the results (lookup intent). `idx` is its
  // position in the keyboard-nav order so it highlights with arrow keys too.
  const askRowAt = (idx: number, primary: boolean) => (
    <button
      type="button"
      role="option"
      aria-selected={idx === activeIndex}
      onClick={goAsk}
      onMouseEnter={() => setActiveIndex(idx)}
      style={{
        ...resultRow,
        flexDirection: 'row', alignItems: 'center', gap: 10,
        background: idx === activeIndex ? 'color-mix(in srgb, var(--parkify-accent) 16%, transparent)' : (primary ? 'color-mix(in srgb, var(--parkify-accent) 8%, transparent)' : 'transparent'),
        borderTop: primary ? 'none' : '1px solid color-mix(in srgb, var(--parkify-accent) 10%, transparent)',
        borderRadius: primary ? 'var(--radius-chip)' : 0,
        margin: primary ? '4px 4px 2px' : '6px 0 0',
        width: primary ? 'calc(100% - 8px)' : '100%',
      }}
    >
      <span aria-hidden style={{
        flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--parkify-accent-light) 0%, var(--parkify-accent) 50%, var(--parkify-accent-strong) 100%)',
        color: '#fff', fontWeight: 800, fontSize: 12, fontFamily: 'monospace',
      }}>a</span>
      <span style={{ ...rowPrimary, color: 'var(--parkify-accent)', fontWeight: 700 }}>
        Ask Avery{value.trim() ? `: “${value.trim()}”` : ''}
      </span>
    </button>
  )

  return (
    // Outer wrapper for relative dropdown positioning and click-outside detection
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', maxWidth: 520 }}>
      <form
        onSubmit={onSubmit}
        role="search"
        aria-label="Ask Avery"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', boxSizing: 'border-box',
          /* Mirrors Austin's demo (CAveryBar): a plain white field with a real
             1.5px border and a text cursor — an INPUT's affordance, not a
             glassy pill's. No blur, no accent glow, no gradient furniture. */
          background: 'var(--color-white, #fff)',
          border: `1.5px solid ${focused ? 'var(--parkify-accent)' : 'var(--color-brand-border)'}`,
          borderRadius: dropdownOpen ? '10px 10px 0 0' : 10,
          padding: '10px 13px',
          cursor: 'text',
          transition: 'border-color 140ms ease',
          fontFamily: "'Montserrat', 'Avenir', sans-serif",
        }}
      >
        {/* Sparkle glyph — the demo's mark for the Avery field. */}
        <span aria-hidden style={{
          flex: '0 0 auto', fontSize: 13, fontWeight: 800, lineHeight: 1,
          color: 'var(--parkify-accent)',
        }}>
          ✦
        </span>

        {/* Input with rotating animated placeholder */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0, height: 24 }}>
          <input
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => {
              setFocused(true)
              // Re-open dropdown if we already have results for the current query
              if (value.length >= 2 && (nativeResults !== null || customers.length > 0)) {
                setDropdownOpen(true)
              }
            }}
            onBlur={() => setFocused(false)}
            aria-label="Ask Avery"
            aria-expanded={dropdownOpen}
            aria-haspopup="listbox"
            autoComplete="off"
            enterKeyHint="send"
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--color-black)', fontSize: 13, fontWeight: 600,
              padding: 0, margin: 0, fontFamily: 'inherit',
            }}
          />
          {showPlaceholder && (
            <span aria-hidden style={{
              position: 'absolute', insetInlineStart: 0, top: 0, height: '100%', maxWidth: '100%',
              display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis', pointerEvents: 'none',
              color: 'var(--color-gray-500)', fontSize: 13, fontWeight: 600,
              opacity: phVisible ? 1 : 0,
              transform: phVisible ? 'translateY(0)' : 'translateY(3px)',
              transition: `opacity ${ASK_FADE_MS}ms ease, transform ${ASK_FADE_MS}ms ease`,
            }}>
              {filterKey ? `Filter ${FILTER_LABELS[filterKey] ?? ''}… or ask Avery` : ASK_SUGGESTIONS[idx]}
            </span>
          )}
        </div>

        {/* No visible send affordance — the demo's bar is a field you type in.
            The submit button stays for Enter-to-submit and screen readers. */}
        <button type="submit" aria-label="Ask Avery" style={{
          position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
          overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }} />
      </form>

      {/* ── Search results dropdown ──────────────────────────────────────────── */}
      {dropdownOpen && (
        <div
          role="listbox"
          aria-label="Search results"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
            background: 'var(--color-white, #fff)',
            border: '1.5px solid var(--color-brand-border)',
            borderTop: '1px solid var(--color-brand-border)',
            /* Mirrors the field's open state above it — same 10px on the outer
               corners, square where the two meet. */
            borderRadius: '0 0 10px 10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxHeight: 420, overflowY: 'auto',
            fontFamily: "'Montserrat', 'Avenir', sans-serif",
            paddingBottom: 6,
          }}
        >
          {/* Question intent → lead with the Ask Avery action. */}
          {asking && askRowAt(0, true)}

          {/* Searching affordance */}
          {searching && (
            <div style={{
              padding: '10px 14px', fontSize: 12, color: 'var(--color-gray-500)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{
                display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                border: '2px solid color-mix(in srgb, var(--parkify-accent) 25%, transparent)',
                borderTopColor: 'var(--parkify-accent)',
                animation: 'avery-spin 0.7s linear infinite',
              }} />
              Searching…
            </div>
          )}

          {/* No matches (suppressed when asking — the Ask row above is the point) */}
          {!searching && hasSearched && totalHits === 0 && !asking && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--color-gray-500)' }}>
              No matches for <strong>{value}</strong>
            </div>
          )}

          {/* Result rows — flat + keyboard-navigable; section headers inserted on change. */}
          {(() => {
            const out: React.ReactNode[] = []
            let lastSection = ''
            resultRows.forEach((r, k) => {
              const idx = firstResultIndex + k
              if (r.section !== lastSection) {
                lastSection = r.section
                out.push(<div key={`h:${r.section}`} style={sectionHeader}>{r.section}</div>)
              }
              out.push(
                <button
                  key={r.key}
                  role="option"
                  aria-selected={idx === activeIndex}
                  style={{ ...resultRow, background: idx === activeIndex ? 'color-mix(in srgb, var(--parkify-accent) 10%, transparent)' : 'transparent' }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={r.onSelect}
                >
                  <span style={rowPrimary}>{r.primary}</span>
                  {r.secondary != null && <span style={rowSecondary}>{r.secondary}</span>}
                </button>,
              )
            })
            return out
          })()}

          {/* Lookup intent → Ask Avery offered as a quiet footer below results. */}
          {showFooterAsk && askRowAt(askIndex, false)}
        </div>
      )}

      {/* Spin keyframe for the searching spinner */}
      <style>{`@keyframes avery-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
